/**
 * Download-Seite — frei zugänglich (kein Login nötig).
 *
 * Zeigt:
 *   - Welche Plattform der User gerade nutzt (Browser/PWA/Android-App)
 *   - Welche App-Version aktuell verfügbar ist
 *   - Welche App-Version installiert ist (falls TWA und beim Start
 *     ?v=X.Y.Z mitgegeben wurde)
 *   - Direkter APK-Download für Android
 *   - Installationshinweise
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchLatestAppVersion,
  getInstalledAppVersion,
  getPlatform,
  platformLabel,
  type AppVersionInfo,
  type PlatformKind,
} from '../lib/platform';
import { hapticTap } from '../lib/haptics';

export default function DownloadPage() {
  const [platform] = useState<PlatformKind>(() => getPlatform());
  const [installedVersion] = useState<string | null>(() => getInstalledAppVersion());
  const [latest, setLatest] = useState<AppVersionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await fetchLatestAppVersion();
        if (cancelled) return;
        if (!info) {
          setError('Keine Versions-Information verfügbar.');
        } else {
          setLatest(info);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Fehler beim Laden');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateAvailable =
    !!latest && !!installedVersion && installedVersion !== latest.latest;
  const upToDate = !!latest && !!installedVersion && installedVersion === latest.latest;

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-8">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">ShiftSync App</h2>
        <Link
          to="/"
          className="text-xs text-slate-400 hover:text-slate-200 underline"
        >
          ← zurück
        </Link>
      </div>

      {/* Plattform-Indikator */}
      <section className="bg-slate-800 rounded-lg p-4">
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">
          Aktuell genutzt über
        </p>
        <div className="flex items-center gap-2">
          <PlatformIcon platform={platform} />
          <p className="text-lg font-semibold">{platformLabel(platform)}</p>
        </div>
        {platform === 'twa' && installedVersion && (
          <p className="text-sm text-slate-400 mt-1">
            Installierte App-Version: <span className="font-medium">v{installedVersion}</span>
          </p>
        )}
        {platform === 'twa' && !installedVersion && (
          <p className="text-xs text-slate-500 mt-1">
            (Versionsinfo der App nicht ermittelbar.)
          </p>
        )}
      </section>

      {/* Latest-Version-Karte */}
      <section className="bg-slate-800 rounded-lg p-4">
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">
          Verfügbare App-Version
        </p>
        {loading && <p className="text-slate-400">Lade…</p>}
        {error && <p className="text-red-400 text-sm">{error}</p>}
        {latest && (
          <>
            <p className="text-2xl font-semibold">v{latest.latest}</p>
            {latest.releaseDate && (
              <p className="text-xs text-slate-500">Veröffentlicht: {latest.releaseDate}</p>
            )}

            {updateAvailable && (
              <p className="text-sm text-amber-300 mt-2">
                Update verfügbar — bitte neue Version installieren.
              </p>
            )}
            {upToDate && (
              <p className="text-sm text-emerald-400 mt-2">App ist auf dem neuesten Stand.</p>
            )}

            {/* Download-Button — nur sinnvoll im Browser oder wenn Update verfügbar */}
            {(platform !== 'twa' || updateAvailable) && (
              <a
                href={latest.downloadUrl}
                onClick={() => hapticTap()}
                className="inline-flex items-center justify-center gap-2 mt-4 w-full sm:w-auto bg-brand hover:bg-brand-dark transition rounded px-4 py-3 text-sm font-medium"
                download
              >
                <DownloadIcon /> APK herunterladen (Android)
              </a>
            )}

            {latest.sha256 && (
              <p className="text-[10px] text-slate-500 mt-2 break-all">
                SHA256: {latest.sha256}
              </p>
            )}

            {latest.changelog && latest.changelog.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-700">
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">
                  Neu in dieser Version
                </p>
                <ul className="text-sm text-slate-300 space-y-1 list-disc list-inside">
                  {latest.changelog.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      {/* Installations-Hinweise — nur wenn aktuell nicht in der App */}
      {platform !== 'twa' && (
        <section className="bg-slate-800 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">
            Installation auf Android
          </p>
          <ol className="text-sm text-slate-300 space-y-2 list-decimal list-inside">
            <li>APK über den Button oben herunterladen.</li>
            <li>
              Beim ersten Mal fragt Android, ob Apps aus dieser Quelle installiert werden dürfen
              — bestätigen.
            </li>
            <li>APK öffnen und installieren.</li>
            <li>App starten — funktioniert offline, hat keine URL-Leiste.</li>
          </ol>
          <p className="text-xs text-slate-500 mt-3">
            iOS wird nicht als App unterstützt — über Safari die Seite öffnen und
            „Zum Home-Bildschirm" hinzufügen.
          </p>
        </section>
      )}

      {/* Fallback wenn kein latest geladen werden konnte */}
      {!loading && !latest && (
        <section className="bg-slate-800 rounded-lg p-4">
          <p className="text-sm text-slate-400">
            Aktuell ist kein Download verfügbar. Wende dich an den Admin.
          </p>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function PlatformIcon({ platform }: { platform: PlatformKind }) {
  if (platform === 'twa') {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/20 text-emerald-400 text-base">
        ✓
      </span>
    );
  }
  if (platform === 'pwa') {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-500/20 text-blue-400 text-base">
        ◌
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-700 text-slate-300">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
      </svg>
    </span>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
